import { useId } from "react";
import { cn } from "@/lib/cn";

/** Filled path data traced from the official AttendeeSync mark (viewBox 0 0 64 64). */
const CLOUD_PATH =
  "M 24.793 14.269 C 22.206 14.695 20.182 15.471 17.975 16.902 C 14.961 18.850 12.161 22.411 11.263 25.410 C 11.095 26.019 11.034 26.064 10.228 26.293 C 7.564 27.038 5.418 28.454 3.775 30.524 C 2.892 31.635 1.918 33.644 1.598 34.998 C 1.218 36.612 1.218 39.427 1.583 41.025 C 2.329 44.191 4.581 47.053 7.580 48.635 C 9.299 49.533 10.639 49.823 13.728 49.914 C 16.788 50.005 17.305 49.883 17.731 48.970 C 18.036 48.331 18.036 47.935 17.731 47.296 C 17.351 46.489 16.772 46.307 14.596 46.307 C 12.222 46.292 10.578 46.002 9.330 45.363 C 6.590 43.978 4.977 41.330 4.977 38.164 C 4.977 36.536 5.205 35.470 5.875 34.131 C 7.138 31.620 9.239 30.174 12.526 29.565 C 13.652 29.352 14.078 28.910 14.489 27.525 C 16.575 20.463 23.256 16.567 30.668 18.089 C 34.199 18.820 37.517 21.148 39.678 24.405 C 41.185 26.688 41.414 26.795 43.499 26.156 C 44.153 25.942 45.097 25.699 45.599 25.592 C 46.923 25.303 49.800 25.349 51.002 25.653 C 54.716 26.627 57.638 29.443 58.627 33.005 C 59.069 34.527 59.099 36.931 58.703 38.392 C 57.897 41.376 55.751 43.917 52.905 45.257 C 51.276 46.002 50.469 46.170 47.502 46.292 C 44.762 46.398 44.473 46.489 44.077 47.311 C 43.788 47.935 43.788 48.346 44.092 48.970 C 44.518 49.883 45.036 50.005 48.095 49.914 C 51.459 49.807 52.722 49.518 55.035 48.331 C 58.643 46.474 61.230 43.248 62.280 39.290 C 62.737 37.601 62.782 34.481 62.371 32.731 C 61.108 27.327 57.090 23.325 51.565 22.000 C 50.059 21.635 46.360 21.635 44.762 22.000 C 44.138 22.153 43.453 22.305 43.240 22.351 C 42.905 22.427 42.753 22.305 42.098 21.392 C 39.450 17.769 35.706 15.365 31.216 14.421 C 29.603 14.071 26.422 13.995 24.793 14.269 Z";

const ARROW_RIGHT_PATH =
  "M 36.528 25.653 C 35.889 26.079 35.645 26.871 35.934 27.586 C 36.041 27.845 36.634 28.515 37.243 29.093 C 37.867 29.656 38.370 30.174 38.370 30.219 C 38.370 30.280 33.758 30.326 28.111 30.326 L 17.868 30.326 L 17.351 30.676 C 16.635 31.163 16.422 31.970 16.833 32.731 C 17.001 33.035 17.305 33.370 17.518 33.476 C 17.807 33.629 20.243 33.674 28.248 33.674 L 38.598 33.674 L 37.259 34.937 C 36.087 36.018 35.904 36.262 35.828 36.794 C 35.676 38.073 36.969 39.062 38.111 38.514 C 38.370 38.392 39.907 37.068 41.520 35.592 C 44.686 32.685 44.884 32.411 44.458 31.376 C 44.244 30.859 38.659 25.653 38.096 25.440 C 37.517 25.227 37.076 25.288 36.528 25.653 Z";

const ARROW_LEFT_PATH =
  "M 25.661 34.253 C 25.417 34.405 23.926 35.714 22.358 37.160 C 19.268 39.990 19.071 40.280 19.497 41.299 C 19.619 41.573 20.927 42.898 22.663 44.496 C 25.813 47.372 26.178 47.600 27.092 47.220 C 27.716 46.976 28.020 46.596 28.111 45.972 C 28.233 45.226 27.913 44.678 26.650 43.537 C 26.072 43.004 25.585 42.532 25.585 42.456 C 25.585 42.395 30.196 42.350 35.843 42.350 L 46.086 42.350 L 46.604 42.000 C 47.319 41.512 47.532 40.706 47.121 39.945 C 46.954 39.640 46.649 39.306 46.436 39.199 C 46.147 39.047 43.712 39.001 35.706 39.001 L 25.356 39.001 L 26.696 37.738 C 27.883 36.642 28.050 36.414 28.111 35.881 C 28.279 34.450 26.863 33.522 25.661 34.253 Z";

type AttendeeSyncIconProps = {
  className?: string;
  /** Gradient mark (default) or mono via currentColor. */
  variant?: "gradient" | "mono";
  /** Accessible name; omit when paired with visible wordmark. */
  title?: string;
  decorative?: boolean;
};

export function AttendeeSyncIcon({
  className,
  variant = "gradient",
  title = "AttendeeSync",
  decorative = false,
}: AttendeeSyncIconProps) {
  const reactId = useId().replace(/:/g, "");
  const gradId = `as-icon-grad-${reactId}`;
  const fill = variant === "mono" ? "currentColor" : `url(#${gradId})`;

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 64 64"
      fill="none"
      className={cn("rs-brand-icon", className)}
      role={decorative ? undefined : "img"}
      aria-hidden={decorative ? true : undefined}
      aria-label={decorative ? undefined : title}
    >
      {!decorative ? <title>{title}</title> : null}
      {variant === "gradient" ? (
        <defs>
          <linearGradient
            id={gradId}
            x1="10"
            y1="32"
            x2="54"
            y2="32"
            gradientUnits="userSpaceOnUse"
          >
            <stop stopColor="#2564EB" />
            <stop offset="1" stopColor="#1590EA" />
          </linearGradient>
        </defs>
      ) : null}
      <path fill={fill} d={CLOUD_PATH} />
      <path fill={fill} d={ARROW_RIGHT_PATH} />
      <path fill={fill} d={ARROW_LEFT_PATH} />
    </svg>
  );
}
